import clsx from "clsx";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
} from "react";
import { createUseStyles } from "$utils/createUseStyles";
import Addicon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ButtonGroup, { ButtonGroupProps } from "@mui/material/ButtonGroup";
import { Button as UIButton } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import clamp from "lodash/clamp";

type Props = {
  alsoOnChange: (qty: number, product: any) => void;
  product: any;
  enableNegatifNumber?: boolean;
  disableInput?: boolean;
  minValue?: number;
  maxValue?: number;
  setpQté?: Dispatch<SetStateAction<number>>;
  debounced?: boolean; // Keep for backward compatibility but not used
} & ButtonGroupProps;

function NumberInput({
  alsoOnChange,
  product,
  enableNegatifNumber = false,
  disableInput = false,
  minValue,
  maxValue,
  setpQté,
  debounced: _debounced,
  ...buttonGroupProps
}: Props) {
  const classes = useStyles();
  const [value, setValue] = useState<string>(
    (product?.customQty || product.qty || 0).toString()
  );

  const onAdd = () => {
    // Use local value state instead of product.qty to prevent NaN issues
    // when clicking +/- buttons rapidly while parent state is updating
    const currentValue = parseInt(value, 10);
    const pQte = Number.isNaN(currentValue) ? 0 : currentValue;

    const newQty =
      maxValue !== undefined ? Math.min(pQte + 1, maxValue) : pQte + 1;
    setValue(newQty.toString());
    alsoOnChange(newQty, product);
  };

  const onSubstract = () => {
    // Use local value state instead of product.qty to prevent NaN issues
    // when clicking +/- buttons rapidly while parent state is updating
    const currentValue = parseInt(value, 10);
    const pQte = Number.isNaN(currentValue) ? 0 : currentValue;

    const minAllowed =
      minValue !== undefined ? minValue : enableNegatifNumber ? -Infinity : 0;
    if (pQte > minAllowed) {
      const newQty = pQte - 1;
      setValue(newQty.toString());
      alsoOnChange(newQty, product);
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Allow empty input but don't change qty - just update local state
    if (e.target.value === "") {
      setValue(e.target.value);
      return;
    }
    const qty = parseInt(e.target.value || "0", 10);
    // Block input other than number or above max value
    // Only update local state, don't call alsoOnChange yet (wait for blur)
    if ((!Number.isNaN(qty) && !maxValue) || qty <= maxValue) {
      setValue(e.target.value);
      if (setpQté) {
        setpQté(qty);
      }
    }
  };

  const onBlurClean = (e: any) => {
    const textValue = e.target.value;
    if (textValue === "") {
      // If empty, use minValue or 0
      const newValue = minValue || 0;
      setValue(newValue.toString());
      if (setpQté) {
        setpQté(newValue);
      }
      alsoOnChange(newValue, product);
    } else {
      // If not-empty, clamp to min or max
      const qty = parseInt(e.target.value || "0", 10);
      // Validate that qty is a valid number
      if (Number.isNaN(qty)) {
        // Reset to current product qty if invalid
        const currentQty = product?.customQty || product.qty || minValue || 0;
        setValue(currentQty.toString());
        if (setpQté) {
          setpQté(currentQty);
        }
        return;
      }
      const newValue = maxValue
        ? clamp(qty, minValue || 0, maxValue)
        : Math.max(qty, minValue || 0);
      setValue(newValue.toString());
      if (setpQté) {
        setpQté(newValue);
      }
      // Only call alsoOnChange if the value actually changed
      const currentQty = product?.customQty || product.qty || 0;
      if (newValue !== currentQty) {
        alsoOnChange(newValue, product);
      }
    }
  };

  useEffect(() => {
    setValue((product?.customQty || product.qty || 0).toString());
  }, [product]);

  return (
    <div className={classes.inputNumber}>
      <ButtonGroup disableElevation variant="outlined" {...buttonGroupProps}>
        <UIButton
          onClick={onSubstract}
          disabled={
            disableInput ||
            ((minValue || minValue === 0) && value === minValue.toString())
          }
        >
          <RemoveIcon />
        </UIButton>
        <InputBase
          type="text"
          inputProps={{
            min: "0",
            step: "1",
            style: { textAlign: "center", background: "#fff" },
          }}
          className={clsx(
            classes.inputClass,
            disableInput ? classes.disableInput : ""
          )}
          defaultValue={value}
          value={value}
          disabled={disableInput}
          // pb du onChange : quand on vide temporairement le champs pour écrire autre chose
          // à la place, ça lance la fonction de suppression de la ligne car ça croit qu'on veut mettre 0
          // mais le onBlur permet pas de lancer la fonction quand on valide juste après avoir modf le champ
          // onBlur={onChange}
          onBlur={(e) => onBlurClean(e)}
          onChange={(e) => {
            onChange(e);
          }}
        />
        <UIButton
          className={classes.lastBtn}
          onClick={onAdd}
          disabled={
            disableInput ||
            ((maxValue || maxValue === 0) && value === maxValue.toString())
          }
        >
          <Addicon />
        </UIButton>
      </ButtonGroup>
    </div>
  );
}

const useStyles = createUseStyles(() => ({
  inputNumber: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  inputClass: {
    borderTop: "solid 1px rgb(0,0,0,0.2)",
    borderBottom: "solid 1px rgb(0,0,0,0.2)",
    flexWrap: "wrap",
  },
  lastBtn: {
    borderLeft: "none !important",
  },
  add: {
    marginTop: "5px !important",
  },
  disableInput: {},
}));

export default NumberInput;
